import React, { useEffect, useState, useMemo } from 'react';

/**
 * ARCHX AI — Matrix Arch Visualizer (Engine Monitor, Layer 2)
 * ------------------------------------------------------------------
 * Builds a dental arch on screen as a case processes — dots (scan points +
 * implant markers) light up, wires connect them into the arch bar, and teeth
 * grow along the curve. Driven by the case's REAL stage/progress.
 *
 *   ingesting   → faint scan-point cloud fades in
 *   detecting   → implant marker nodes light up across the arch
 *   registering → wires connect the markers (the bar forms)
 *   designing   → teeth grow along the arch
 *   done        → whole arch locks in, green
 *   failed      → arch tints red, animation stops
 *
 * Pure SVG — no 3D deps. Props: { stage, progress, state }.
 */

const VB_W = 460, VB_H = 280;
const CX = 230, CY = 200, RX = 175, RY = 135;

// A point on the arch curve. t in [0,1] traces left→apex→right (∩ horseshoe).
function archPoint(t, radialOffset = 0) {
  const theta = Math.PI + Math.PI * t;
  const rx = RX + radialOffset, ry = RY + radialOffset;
  return { x: CX + rx * Math.cos(theta), y: CY + ry * Math.sin(theta) };
}

const DEFAULT_IMPLANTS = 6;
const N_TEETH = 12;
const N_RIDGE = 54;

export default function ArchVisualizer({ stage, progress = 0, state = 'processing', markers = null }) {
  // Use the REAL detected marker count once the engine reports it (clamped to a
  // sane arch range); fall back to a representative 6 before detection.
  const N_IMPLANTS = markers && markers > 0 ? Math.max(2, Math.min(8, markers)) : DEFAULT_IMPLANTS;
  // Smoothly ease a local value toward the real progress so stage jumps glide.
  const [anim, setAnim] = useState(0);
  useEffect(() => {
    const target = state === 'done' ? 100 : (progress || 0);
    const id = setInterval(() => {
      setAnim(a => {
        const next = a + (target - a) * 0.08;
        return Math.abs(target - next) < 0.4 ? target : next;
      });
    }, 33);
    return () => clearInterval(id);
  }, [progress, state]);

  const failed = state === 'failed';
  const done = state === 'done';
  const active = !failed && !done;
  const accent = failed ? '#ef4444' : done ? '#22c55e' : '#fbbf24';

  // Precompute geometry once.
  const implants = useMemo(
    () => Array.from({ length: N_IMPLANTS }, (_, i) => archPoint(i / (N_IMPLANTS - 1))),
    [N_IMPLANTS]
  );
  const ridge = useMemo(
    () => Array.from({ length: N_RIDGE }, (_, j) => {
      const t = j / (N_RIDGE - 1);
      const jitter = (Math.sin(j * 12.9898) * 43758.5453 % 1) * 14 - 7; // deterministic pseudo-jitter
      return { ...archPoint(t, jitter), t };
    }),
    []
  );
  const teeth = useMemo(
    () => Array.from({ length: N_TEETH }, (_, k) => {
      const t = (k + 0.5) / N_TEETH;
      const p = archPoint(t);
      const tan = archPoint(t + 0.01);
      const angle = Math.atan2(tan.y - p.y, tan.x - p.x) * 180 / Math.PI;
      return { ...p, angle, t };
    }),
    []
  );

  // Reveal fractions from eased progress.
  const cloud = clamp((anim - 0) / 25);                 // 0..25
  const detect = clamp((anim - 25) / 25);               // 25..50
  const wire = clamp((anim - 55) / 20);                 // 55..75
  const design = clamp((anim - 75) / 25);               // 75..100

  // Build the connecting wire path through implants, revealed by `wire`.
  const wirePath = useMemo(() => {
    return implants.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  }, [implants]);

  return (
    <svg viewBox={`0 0 ${VB_W} ${VB_H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      <defs>
        <radialGradient id="archGlow" cx="50%" cy="60%" r="60%">
          <stop offset="0%" stopColor={accent} stopOpacity="0.10" />
          <stop offset="100%" stopColor={accent} stopOpacity="0" />
        </radialGradient>
        <filter id="softGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2.2" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      {/* ambient glow */}
      <rect x="0" y="0" width={VB_W} height={VB_H} fill="url(#archGlow)" />

      {/* faint full-arch scaffold (always present, very dim) */}
      <path d={wirePath} fill="none" stroke={accent} strokeOpacity="0.12" strokeWidth="1" strokeDasharray="3 5" />

      {/* Stage 1 — scan-point cloud (ingesting) */}
      <g opacity={cloud}>
        {ridge.map((p, i) => (
          <circle key={`r${i}`} cx={p.x} cy={p.y} r="1.6"
            fill={accent} fillOpacity={0.35}
            style={active ? { animation: `archTwinkle ${2 + (i % 5) * 0.3}s ${i * 0.02}s infinite` } : undefined} />
        ))}
      </g>

      {/* Stage 3 — wires connecting implants (registering) */}
      <path d={wirePath} fill="none" stroke={accent} strokeWidth="2"
        strokeOpacity={0.9} strokeDasharray="1000" strokeDashoffset={1000 * (1 - wire)}
        style={{ transition: 'stroke-dashoffset .4s linear' }} filter="url(#softGlow)" />

      {/* Stage 4 — teeth grow along the arch (designing) */}
      <g>
        {teeth.map((t, k) => {
          const rev = clamp((design * N_TEETH) - k);
          if (rev <= 0) return null;
          return (
            <g key={`t${k}`} transform={`translate(${t.x} ${t.y}) rotate(${t.angle + 90}) scale(${rev})`}>
              <rect x="-5" y="-9" width="10" height="18" rx="4"
                fill={done ? '#22c55e' : '#fde68a'} fillOpacity={done ? 0.9 : 0.8}
                stroke={accent} strokeWidth="0.6" />
            </g>
          );
        })}
      </g>

      {/* Stage 2 — implant marker nodes (detecting) */}
      <g>
        {implants.map((p, i) => {
          const rev = clamp((detect * N_IMPLANTS) - i);
          if (rev <= 0) return null;
          return (
            <g key={`i${i}`} opacity={rev}>
              <circle cx={p.x} cy={p.y} r={7} fill="none" stroke={accent} strokeWidth="1.5"
                style={active ? { animation: `archPing 1.8s ${i * 0.15}s infinite` } : undefined} />
              <circle cx={p.x} cy={p.y} r={3.2} fill={accent} filter="url(#softGlow)" />
            </g>
          );
        })}
      </g>

      {/* scanning sweep line while active */}
      {active && (
        <line x1="0" y1="0" x2={VB_W} y2="0" stroke={accent} strokeOpacity="0.5" strokeWidth="2"
          style={{ animation: 'archSweep 3.2s linear infinite' }} />
      )}

      <style>{`
        @keyframes archTwinkle { 0%,100%{opacity:.25} 50%{opacity:.7} }
        @keyframes archPing { 0%{r:7;opacity:.9} 70%{r:13;opacity:0} 100%{r:13;opacity:0} }
        @keyframes archSweep { 0%{transform:translateY(40px)} 100%{transform:translateY(235px)} }
      `}</style>
    </svg>
  );
}

function clamp(v) { return Math.max(0, Math.min(1, v)); }
